<script setup>
import { ref, reactive, computed, watch, inject } from 'vue';
import axios from 'axios';
import AvailabilityCalendar from './AvailabilityCalendar.vue';
import RoomSelector from './RoomSelector.vue';
import FormCard from '@/components/FormCard.vue';
import { useAvailability } from '../composables/useAvailability.js';
import { state, addToBasket, updateBasketItem } from '@/shared/basketStore.js';
import { replacePanelContent } from '@/shared/panelStack.js';
import { formatPrice } from '@/shared/formatPrice.js';
import { perPersonPrice } from '@/shared/perPersonPrice.js';
import BasketPanel from '@/components/BasketPanel.vue';

const props = defineProps({
    product:              { type: Object,  required: true },
    locations:            { type: Array,   default: () => [] },
    // Null until the customer picks one. The search page always has a location by the
    // time a panel can open, but the product page can be reached without ever searching,
    // in which case the panel's own Choose Location stage is where it gets set.
    locationId:           { type: Number,  default: null },
    initialMonth:         { type: String,  default: null },
    initialPeople:        { type: Number,  default: null },
    availabilityApiUrl:   { type: String,  required: true },
    spaBreakRoomsApiUrl:  { type: String,  required: true },
    // Only needed by a family whose variant_choice is 'customer'. Nullable so a caller with
    // no such product on the page is not forced to thread a URL it will never use.
    variantsApiUrl:       { type: String,  default: null },

    // --- Edit mode (checkout-location-date-editing-scope-technical.md §9) ---
    //
    // Set together, by whoever opened this panel against an existing basket item rather than
    // against a fresh search. `basketItemGuid` is the switch: with one, Continue amends that
    // item instead of adding a new one, and the panel opens showing what was already chosen
    // rather than an empty date card.
    basketItemGuid:       { type: String,  default: null },
    initialDate:          { type: String,  default: null },
    initialVariantId:     { type: Number,  default: null },
    // Which card to open on, so the control the customer actually picked is the one waiting
    // for them. Anything else arrives collapsed, showing its current value.
    openStage:            { type: String,  default: null },
    // Where the item is being edited from. 'checkout' reloads the page on success, because
    // that page built its treatment and time pickers against the OLD spa and day at load
    // time - the same reason LockedSlotDisplay reloads after cancelling a reservation.
    editedFrom:           { type: String,  default: 'basket' },
});

const { dates, loading, error, fetchDates } = useAvailability(props.availabilityApiUrl);

const currentMonth        = ref(props.initialMonth ?? new Date().toISOString().slice(0, 7));
// Only show locations where this package is actually offered.
const availableLocations = computed(() =>
    props.product.location_ids?.length
        ? props.locations.filter(l => props.product.location_ids.includes(l.id))
        : props.locations
);

// The spa the customer is shopping at, but only if this product is actually sold there.
// A product page renders for a product the chosen spa does not sell (the header says
// "22 Locations" rather than naming the spa - see ProductController), and that spa has no
// <option> in the select below. Seeding it regardless left the select bound to a value
// with no matching option: it rendered blank, the "Choose a spa..." placeholder was
// skipped because the value was not null, the card collapsed as though answered, and
// Confirm was enabled on a summary showing nothing. Falling back to null makes the panel
// ask the question instead.
const seededLocationId = props.locationId !== null
    && availableLocations.value.some(l => l.id === props.locationId)
    ? props.locationId
    : null;

const selectedLocationId  = ref(seededLocationId);
const selectedPeople      = ref(props.initialPeople ?? props.product.people ?? 1);
// Seeded from the props in edit mode, null when adding. The variant matters for a family
// whose members differ in substance, where the panel asks outright; for a day-range family
// the watcher below re-derives it from the date, so seeding it costs nothing either way.
const selectedDate      = ref(props.initialDate);
const selectedVariantId = ref(props.initialVariantId);
const selectedRoom      = ref(null);
const adding            = ref(false);

// Whether this panel is amending an existing basket item rather than adding a new one.
const editing = computed(() => props.basketItemGuid !== null);

// Which stages are collapsed. The panel owns this rather than each card, because
// only the panel knows what the stages are - the same job confirmFormCard() does
// imperatively for server-rendered cards.
//
// Guests starts collapsed: it was already answered by the search that got the
// customer here, so it shows as a settled value and reopens only when the customer
// deliberately picks its edit control. The date is what this panel exists to ask,
// so it starts open.
//
// Location starts collapsed on the same reasoning, but only when there IS one.
// Opened from a product page the customer may never have searched, and a collapsed
// card summarising nothing would give them no way to see what the panel is waiting
// for. The date card is withheld entirely until a spa is chosen (see the template),
// so the open Location card is the only question on screen.
//
// Editing starts every card collapsed except the one whose control was picked: all of them
// are already answered, so a card standing open would be asking a question the customer has
// not chosen to reopen. `openStage` names the exception.
const stages = reactive({
    location: props.openStage ? props.openStage !== 'location' : seededLocationId !== null,
    guests:   props.openStage ? props.openStage !== 'guests' : true,
    variant:  props.openStage ? props.openStage !== 'variant' : false,
    // The date is what this panel exists to ask when adding, so it opens. When editing it is
    // already answered, and only opens if that is the control the customer picked.
    date:     props.openStage ? props.openStage !== 'date' : false,
});

// Whether this family's members differ in substance rather than only by day range, in which
// case the calendar cannot tell them apart and the customer has to be asked outright. Set per
// family in the admin and seeded by `packages:variant-choice` - see docs/product-page.md.
//
// When false nothing below runs and the panel behaves exactly as it always has.
const customerChoosesVariant = computed(() =>
    props.product.variant_choice === 'customer' && !!props.variantsApiUrl
);

const variants        = ref([]);
const variantsLoading = ref(false);
const variantsError   = ref(null);

// Which options exist depends on the spa and the party size, not on the month: a member can
// be sold at 41 spas and not the other three. Deliberately NOT part of the availability call -
// answering it there would mean asking Phorest once per member and discarding the result,
// seven calls for one "Summer Spa Sale" month view.
async function fetchVariants(locationId, people) {
    if (!customerChoosesVariant.value || !locationId) return;

    variantsLoading.value = true;
    variantsError.value   = null;

    try {
        const response = await axios.get(props.variantsApiUrl, {
            // `type` tells the endpoint which catalogue to ask: a package family and a
            // treatment collection answer the same question from different tables. apiType
            // already maps 'spa-day'/'spa-offer' back to a package for the availability call.
            params: { product_id: props.product.id, location_id: locationId, people, type: apiType.value },
        });
        variants.value = response.data.variants;

        // Nothing is preselected. The whole point is that the customer chooses, and defaulting
        // to the cheapest would reintroduce the bug in a quieter form: they would land on a
        // calendar for an option they never picked.
        if (!variants.value.some(v => v.id === selectedVariantId.value)) {
            selectedVariantId.value = null;
        }

        // The exception, and the one case where choosing FOR the customer is right: a single
        // option is not a choice. The card below is withheld at a length of one, so without
        // this the panel dead-ends - no picker to answer and no calendar, because the Choose
        // Date card is gated on selectedVariantId. Measured across the estate, 12 of the 800
        // spa/collection combinations that sell a collection at all offer exactly one option
        // there, and package families reach the same state through their own per-spa filters.
        if (variants.value.length === 1) {
            selectedVariantId.value = variants.value[0].id;
        }
    } catch {
        variantsError.value = 'Could not load options. Please try again.';
        variants.value = [];
    } finally {
        variantsLoading.value = false;
    }
}

// The calendar highlights days, both ends included, so a spa break's nights
// convert: a 1-night stay covers two days (arrive + leave). Products without a
// nights field are single-date.
const stayDays = computed(() => (props.product.nights ?? 0) + 1);

// A voucher the customer already holds covers this product, so it has no price to show
// anywhere in here - not on the calendar's days, not in the footer totals, not against a
// variant. Arrives on the product payload, which every call site passes, so all seven of
// them are covered without threading a prop through each. See ProductPayloadService.
const voucherCovered = computed(() => Boolean(props.product.voucher_provider));

// The calendar prints per-person rates, as its own copy promises - the availability
// entries hold party totals. `dates` itself stays untouched: the line total and the
// variant id are both read from it.
//
// A covered product's days carry no price at all: AvailabilityCalendar renders an empty
// string for null, so the cells and their aria-labels lose the figure together. The
// entries keep theirs, because the variant id is read from the same object and Continue
// still has to know which day was picked.
const calendarDates = computed(() =>
    Object.fromEntries(
        Object.entries(dates.value).map(([date, entry]) => [
            date,
            {
                ...entry,
                price: voucherCovered.value
                    ? null
                    : perPersonPrice(entry.price, selectedPeople.value),
            },
        ])
    )
);

// For a day-range family the chosen date decides which variant is being booked, so it follows
// the calendar's selection rather than being set alongside it.
//
// Skipped entirely when the customer chooses: there the variant is settled BEFORE the calendar
// and the calendar is already scoped to it, so letting the date write back here would overwrite
// their answer with the same value at best, and drop it on a cleared date at worst.
watch(selectedDate, (date) => {
    if (customerChoosesVariant.value) return;

    selectedVariantId.value = date ? (dates.value[date]?.variant_id ?? props.product.id) : null;
});

// Picking a date settles that stage, so it collapses to show the chosen day.
// There is no Confirm button on this card, unlike location and guests - the
// selection itself is unambiguous, where a <select> fires change on every
// keyboard arrow press and would collapse the card mid-navigation.
watch(selectedDate, (date) => {
    if (date) stages.date = true;
});

// A date was only ever available for the location and guest count it was chosen against,
// and the variant behind it may not serve the new ones. Clearing the date reopens the
// calendar and drops the variant through the watcher above.
watch([selectedLocationId, selectedPeople], () => {
    selectedDate.value = null;
    selectedRoom.value = null;
    stages.date = false;

    // The option card reopens because the previous answer may not be on offer at the new spa
    // or party size. The refetch itself is the dedicated watcher further down, so that a
    // location change does not fire two identical requests.
    if (customerChoosesVariant.value) {
        stages.variant = false;
    }
});

// Changing the option changes which dates and prices exist, so a date chosen against the
// previous one cannot stand.
watch(selectedVariantId, () => {
    if (!customerChoosesVariant.value) return;

    selectedDate.value = null;
    stages.date = false;
});

// Spa days and spa offers are both ProductType::Package server-side - the split is a
// listing concern only (docs/spa-offers.md), and the availability API knows nothing of it.
const apiType = computed(() => {
    if (props.product.type === 'spa-day' || props.product.type === 'spa-offer') return 'package';
    if (props.product.type === 'spa-break') return 'spa-break';
    return 'treatment';
});

const continuePayload = computed(() => {
    if (!selectedDate.value || !selectedVariantId.value) return null;
    if (apiType.value === 'spa-break' && !selectedRoom.value) return null;

    const payload = {
        product_id:  selectedVariantId.value,
        location_id: selectedLocationId.value,
        date:        selectedDate.value,
        type:        apiType.value,
    };

    if (apiType.value === 'spa-break') {
        payload.room_code = selectedRoom.value.code;
        payload.rate_code = selectedRoom.value.rateCode;
        if (selectedRoom.value.configuration) {
            payload.room_configuration = selectedRoom.value.configuration;
        }
    }

    return payload;
});

// A message about what the change cost, held back so the customer reads it before the page
// goes out from under them. Only ever set when editing from checkout, where success means a
// reload - a basket edit hands the notice to BasketSummary instead, which stays on screen.
const pendingNotice = ref(null);

function reloadAfterNotice() {
    window.location.reload();
}

// "Continue" reads as "and then what" for an amendment - the customer is finishing a change,
// not carrying on through a funnel.
const continueLabel = computed(() => {
    if (adding.value) return editing.value ? 'Saving…' : 'Adding…';

    return editing.value ? 'Save Changes' : 'Continue';
});

// Commits the current selection: adds it to the basket, or amends the item this panel was
// opened against (see the edit-mode props).
//
// On a successful ADD the panel's contents are swapped for the basket in place, rather than
// a new panel stacking on top: the selection has been committed, so there is nothing here to
// come back to, and leaving a submitted form underneath would only invite a duplicate basket
// item. This component just gets unmounted, so there's no local selection to reset either.
//
// A successful EDIT does the same from the basket. From checkout it reloads the page instead:
// that page's treatment and time pickers were built at load time against the spa and day this
// change has just replaced, so nothing on it can be trusted until it is rebuilt. Same reason,
// and same handling, as LockedSlotDisplay after cancelling a reservation.
async function onContinue() {
    if (!continuePayload.value || adding.value) return;

    adding.value = true;
    const saved = editing.value
        ? await updateBasketItem(props.basketItemGuid, continuePayload.value)
        : await addToBasket(continuePayload.value);
    adding.value = false;

    if (!saved) return;

    if (editing.value && props.editedFrom === 'checkout') {
        // Reload, unless there is something the customer needs to see first.
        if (state.basketNotice) {
            pendingNotice.value = state.basketNotice;

            return;
        }

        window.location.reload();

        return;
    }

    replacePanelContent({ component: BasketPanel, title: 'Your Basket' });
}

// The Continue button belongs in the panel's sticky footer, so it stays reachable
// without scrolling past the calendar. Null outside a panel, in which case the teleport
// is disabled and it renders inline.
const panelFooter = inject('popOutPanelFooter', null);

// Footer totals, mirroring the server-rendered panel's (partials/booking-panel.blade.php).
// The chosen date's availability entry carries the party total for the selected guest
// count, already the cheapest across that date's variants (AvailabilityService) - the same
// figure BasketController stores on Continue. Per Person is derived from it, as the
// checkout's Booking Summary derives its own.
//
// Null until a date is picked: before that there is no single rate to quote, since the
// calendar's prices vary by day. Null rather than 0 so the template can withhold the
// table entirely instead of showing £0.00.
const linePrice = computed(() => {
    if (!selectedDate.value) return null;

    return dates.value[selectedDate.value]?.price ?? null;
});

const perPerson = computed(() => perPersonPrice(linePrice.value, selectedPeople.value));

// Spa breaks are deliberately excluded: their total depends on the chosen room, and
// RoomSelector emits {code, rateCode, configuration} with no price in it, so there is
// no way to total one correctly from here. Quoting the break's base rate and silently
// dropping a room's extra charge would be worse than showing nothing - a wrong total is
// harder to notice than a missing one. Restore this once RoomSelector emits its price.
const showTotals = computed(() =>
    ! voucherCovered.value && props.product.type !== 'spa-break' && linePrice.value !== null
);

// The guest counts this product's family actually serves. Occupancy is fixed per package
// record, so anything not in the family is unbookable rather than merely unusual.
const peopleOptions = computed(() =>
    props.product.people_options?.length
        ? props.product.people_options
        : [props.product.people ?? 1]
);

const guestLabel = (people) => (people === 1 ? '1 Guest' : `${people} Guests`);

// Only worth a card when there is more than one answer.
const showGuestPicker = computed(() =>
    props.product.type !== 'spa-break' && peopleOptions.value.length > 1
);

// A spa break's count lives on the record; a package's is whatever its family settled on.
const fixedPeople = computed(() =>
    props.product.type === 'spa-break' ? (props.product.people ?? 1) : selectedPeople.value
);

// The collapsed titles: each stage's chosen value, in place of the question the
// open card asks. No location summary - the locations payload carries id, name and
// phorest_branch_id only, so there is no venue line to show (see
// docs/blade-to-vue-components.md).
const selectedLocationName = computed(() =>
    props.locations.find(l => l.id === selectedLocationId.value)?.name ?? null
);

const selectedVariantName = computed(() =>
    variants.value.find(v => v.id === selectedVariantId.value)?.name ?? null
);

// Built from the date parts rather than parsed from the ISO string: an ISO date
// string is treated as UTC, which lands on the previous day in any negative-offset
// timezone. Multi-day stays show the arrival date only.
const selectedDateLabel = computed(() => {
    if (!selectedDate.value) return null;

    const [year, month, day] = selectedDate.value.split('-').map(Number);

    return new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(new Date(year, month - 1, day));
});

// Which product the calendar is asked about. For a day-range family it is the family head and
// the API folds the siblings in, cheapest per date winning. Where the customer chooses, it is
// their chosen member and the API is told not to fold at all, so the dates and the price
// belong to the option they actually picked.
const calendarProductId = computed(() =>
    customerChoosesVariant.value ? selectedVariantId.value : props.product.id
);

// `variants` is a dependency, not just a guard: a location change reloads the option list
// asynchronously, so without it the calendar would fire once against the previous spa's
// option and then never re-fire once the new list settled.
watch(
    [currentMonth, selectedLocationId, selectedPeople, calendarProductId, variants],
    ([month, locationId, people, productId, options]) => {
        // Availability is meaningless without a branch, and asking anyway returns a
        // validation error the customer would see as "Could not load availability" before
        // they had done anything wrong.
        if (! locationId) return;

        if (customerChoosesVariant.value) {
            // Until the option is answered there is no product to ask about. The calendar is
            // withheld from the page in this state rather than shown empty.
            //
            // And the answer has to still be on the list: a spa change reloads the options
            // before it clears a selection that is no longer sold there, and asking for that
            // option's dates in the gap would quote a package this spa does not offer.
            if (! productId || ! options.some(option => option.id === productId)) return;
        }

        fetchDates(apiType.value, productId, locationId, month, people, customerChoosesVariant.value);
    },
    { immediate: true }
);

// The option list is the first thing this panel needs, so it is fetched at setup rather than
// waiting for a location or guest change that may never come - the search page has already
// answered both by the time a panel can open.
watch(
    [selectedLocationId, selectedPeople],
    ([locationId, people]) => fetchVariants(locationId, people),
    { immediate: true }
);
</script>

<template>
    <FormCard
        title="Choose Location"
        icon="location"
        :completed="stages.location"
        :completed-title="selectedLocationName"
        @edit="stages.location = false"
    >
        <select v-model="selectedLocationId">
            <!-- Only while nothing is chosen, which happens when the panel is opened from a
                 product page rather than a search. Without it the select shows the first
                 location while the model is still null, so the customer believes they have
                 picked one and the calendar stays empty with no explanation. Removed from
                 the list once a choice is made, since a location cannot be un-picked. -->
            <option v-if="selectedLocationId === null" :value="null" disabled>Choose a spa...</option>
            <option v-for="loc in availableLocations" :key="loc.id" :value="loc.id">
                {{ loc.name }}
            </option>
        </select>
        <!-- Nothing to confirm until a location is chosen - confirming null would collapse
             the card to an empty summary and hide the one thing the panel needs. -->
        <button
            type="button"
            class="confirm_button"
            :disabled="selectedLocationId === null"
            @click="stages.location = true"
        >Confirm</button>
    </FormCard>

    <FormCard
        v-if="showGuestPicker"
        title="Number of Guests"
        icon="person"
        :completed="stages.guests"
        :completed-title="guestLabel(selectedPeople)"
        @edit="stages.guests = false"
    >
        <select v-model="selectedPeople">
            <option v-for="n in peopleOptions" :key="n" :value="n">
                {{ n }}
            </option>
        </select>
        <button type="button" class="confirm_button" @click="stages.guests = true">Confirm</button>
    </FormCard>

    <!-- A fixed guest count states itself instead of offering a card whose edit control
         could not do anything: spa breaks carry the count on the record, and a package
         family may serve only one occupancy. -->
    <p v-else>
        For {{ fixedPeople === 1 ? '1 person' : `${fixedPeople} people` }}
    </p>

    <p v-if="variantsError" class="form_error">{{ variantsError }}</p>

    <!-- The option question, for a family whose members offer different things - a choice of
         treatment, a longer one, an added lunch. Before the calendar rather than after, because
         which dates and prices exist depends on the answer, and V1 asked in this order too
         (partials/parent-package-selector.blade.php).

         Withheld while loading and when the list has one entry or none: a family can be
         customer-chosen overall and still offer exactly one option at this spa for this party
         size, and a card with a single radio is a question with no answer to give.

         Options are named by the package's own name for now, which is V1's behaviour and is
         not customer-facing copy ("Spa Sale 2026 - 20-minute Elemis Facial - School's Out!").
         A per-member label field is the deferred follow-up - see docs/product-page.md.

         The list is the design system's shared .check_list look (includes/form.scss),
         matching GiftBookingPanel's. -->
    <FormCard
        v-if="customerChoosesVariant && !variantsLoading && variants.length > 1"
        title="Choose your option"
        icon="robe"
        :completed="stages.variant"
        :completed-title="selectedVariantName"
        @edit="stages.variant = false"
    >
        <div class="check_list">
            <label v-for="variant in variants" :key="variant.id">
                <!-- Completed on change rather than a watch, so that fetchVariants() clearing
                     the selection for a new spa cannot collapse a stage nobody has answered. -->
                <input
                    type="radio"
                    :name="`variant_${product.id}`"
                    :value="variant.id"
                    v-model="selectedVariantId"
                    @change="stages.variant = true"
                />
                <span class="check_list_option_name">{{ variant.name }}</span>
                <span v-if="variant.price && !voucherCovered" class="check_list_option_price">{{ formatPrice(variant.price) }}</span>
            </label>
        </div>
    </FormCard>

    <p v-if="error" class="form_error">{{ error }}</p>

    <!-- Held back until a spa is chosen, and until the option is answered where there is one
         to answer. Availability is never fetched without a location, so an empty calendar here
         would render every day struck out and read as "no availability" rather than "we have
         not asked you yet" - the same silent-wrong-answer failure the option guard exists to
         remove. Opened from a product page without a prior search, this leaves the open
         Location card as the only question on screen. -->
    <FormCard
        v-if="selectedLocationId !== null && (!customerChoosesVariant || selectedVariantId)"
        title="Choose Date"
        icon="calendar"
        :completed="stages.date"
        :completed-title="selectedDateLabel"
        @edit="stages.date = false"
    >
        <!-- Withheld with the prices it describes: a covered product's calendar has no
             rates on it, so the line would explain something that is not there. -->
        <p v-if="!voucherCovered">All prices represent our best per-person daily rates.</p>
        <p v-else class="booking_panel_voucher_note">
            Paid for by your {{ product.voucher_provider }} voucher
        </p>

        <AvailabilityCalendar
            v-model="selectedDate"
            :dates="calendarDates"
            :month="currentMonth"
            :loading="loading"
            :days="stayDays"
            @month-change="currentMonth = $event"
        />
    </FormCard>

    <!-- The room is a stage of its own, so it gets a card, but RoomSelector's own
         markup still carries its Tailwind - that component is its own port. No icon:
         nothing bed- or room-shaped exists on the Icon enum yet.
         It never collapses, because RoomSelector emits codes rather than a display
         name, so there is no value-shaped title to collapse to. form-card.scss hides
         the edit control on an open card, so nothing dead is left on screen. -->
    <FormCard
        v-if="product.type === 'spa-break' && selectedDate"
        title="Choose Your Room"
    >
        <RoomSelector
            v-model="selectedRoom"
            :api-url="spaBreakRoomsApiUrl"
            :spa-break-id="product.id"
            :location-id="selectedLocationId"
            :date="selectedDate"
        />
    </FormCard>

    <p v-if="state.basketError" class="form_error">{{ state.basketError }}</p>

    <Teleport :to="panelFooter" :disabled="!panelFooter">
        <!-- Totals above the button, as in the server-rendered panel's footer. Withheld
             until a date settles the rate, and for spa breaks entirely - see showTotals. -->
        <table v-if="showTotals" class="summary_table">
            <tbody>
                <tr>
                    <th>Per Person</th>
                    <td>{{ formatPrice(perPerson) }}</td>
                </tr>
                <tr>
                    <th>Subtotal</th>
                    <td>{{ formatPrice(linePrice) }}</td>
                </tr>
            </tbody>
        </table>

        <!-- The edit succeeded but cost the customer their discount code. Shown here in
             place of the Continue button, because the next thing this does is reload the
             checkout page and a message that vanishes with it was never really shown.
             Acknowledging it is the only way on, deliberately. -->
        <template v-if="pendingNotice">
            <p class="form_error">{{ pendingNotice }}</p>
            <button type="button" class="button primary block" @click="reloadAfterNotice">
                Continue
            </button>
        </template>

        <button
            v-else-if="continuePayload"
            type="button"
            :disabled="adding"
            class="button primary block"
            @click="onContinue"
        >
            {{ continueLabel }}
        </button>
    </Teleport>
</template>
